import { useDispatch, useSelector } from 'react-redux';
import valuationSlice, {
  valuationEffects,
  valuationSelectors,
} from 'features/valuation/slice';
import Popover from '@deprecated-ui/common/Popover';
import Button from '@deprecated-ui/common/Button';
import {
  effects as valuationWidgetEffects,
  selectors as valuationWidgetSelectors,
} from 'global/MainLayout/SideWidget/Valuation/slice';
import { useState } from 'react';
import handleErrorMessageAPI from 'global/AlertErrorMessage';
import SUCCESS_MESSAGE from 'constants/successMessage';
import ValuationItem from './ValuationItem';
import Modal from '../Modal';

interface PopoverValuationProps {
  children: React.ReactNode;
}

function PopoverValuation({ children }: PopoverValuationProps) {
  const dispatch = useDispatch();
  const {
    getValuationTemplate: { data: valuationTemplates },
  } = useSelector(valuationWidgetSelectors);
  const { valuationDetail } = useSelector(valuationSelectors);
  const {
    resetValuationDetail,
    resetValuationResult,
    resetTemplateResult,
    resetValuationSymbol,
    setValuationMetric,
    setValuationSymbol,
    setValuationNote,
    setValuationResult,
    setValuationMetricKeyword,
  } = valuationSlice.actions;

  const [visible, setVisible] = useState(false);
  const [selectedId, setSelectedId] = useState('');
  const [selectedName, setSelectedName] = useState('');

  const handleSelect = async (id) => {
    const getDetail = await dispatch(
      valuationEffects.getValuationDetailTemplate(id),
    );

    // @ts-ignore
    const { error, data } = getDetail.payload;

    if (!error) {
      const payloadMetric = {
        comp_id: data.metric.company.id,
        item: data.metric.item,
      };
      dispatch(setValuationMetric(payloadMetric));
      dispatch(setValuationSymbol(data.metric.company));

      const resultData = data.result.reduce((obj, curr) => {
        // eslint-disable-next-line no-param-reassign
        obj[curr.name] = curr.value;
        return obj;
      }, {});

      dispatch(
        setValuationResult({
          ...resultData,
          symbol: data.metric.company.symbol,
        }),
      );
      dispatch(setValuationNote(data.note));
      dispatch(setValuationMetricKeyword(data.metric.company.symbol));
      dispatch(resetTemplateResult());
    }
  };

  const handleShowDelete = (id, name) => {
    setVisible(true);
    setSelectedId(id);
    setSelectedName(name);
  };

  const handleDeleteValuation = async () => {
    const activeId = valuationDetail.data?.valuation?.id || 0;

    const del = await dispatch(
      valuationEffects.deleteValuationDetailTemplate(Number(selectedId)),
    );

    // @ts-ignore
    const { error } = del.payload;

    if (!error) {
      setVisible(false);
      handleErrorMessageAPI(
        `Template ${selectedName} has been deleted`,
        SUCCESS_MESSAGE.ALERT_GREEN,
      );

      // Re fetch the list
      dispatch(valuationWidgetEffects.getValuationTemplate());

      if (Number(selectedId) === Number(activeId)) {
        // Reset state
        dispatch(resetValuationSymbol());
        dispatch(resetValuationDetail());
        dispatch(resetValuationResult());
        dispatch(resetTemplateResult());
      }
    }
  };

  const handleCancel = () => {
    setVisible(false);
    setSelectedId('');
    setSelectedName('');
  };

  const handleAddNew = () => {
    dispatch(resetValuationSymbol());
    dispatch(resetValuationDetail());
    dispatch(resetValuationResult());
    dispatch(resetTemplateResult());
    dispatch(setValuationNote(''));
    dispatch(setValuationMetricKeyword(''));
  };

  const content = (
    <>
      <div style={{ overflow: 'auto', maxHeight: '100px' }}>
        {valuationTemplates.map((template) => (
          <ValuationItem
            template={template}
            onDelete={handleShowDelete}
            onClick={handleSelect}
          />
        ))}
      </div>
      <div style={{ padding: '4px' }}>
        <Button type="primary" block onClick={handleAddNew}>
          Create New Valuation
        </Button>
      </div>
    </>
  );

  return (
    <>
      <Popover
        zIndex={1}
        content={content}
        trigger="click"
        placement="bottomLeft"
        overlayStyle={{
          width: '240px',
          border: 'solid 1px #dadada',
          background: '#fff',
        }}
      >
        {children}
      </Popover>
      <Modal
        isVisible={visible}
        cancelText="CANCEL"
        okText="DELETE"
        modalTitle="Delete Valuation"
        onClickCancel={handleCancel}
        onClickOk={handleDeleteValuation}
        isDeleteMode
        initValuationName={selectedName}
      />
    </>
  );
}

export default PopoverValuation;
